import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Home, Feather, Plane, Baby, Briefcase, HeartHandshake, TrendingDown, Accessibility, FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import { api, errMsg, downloadExcel, pad, rupiah } from "@/lib/api";
import { RT_LIST, KELOMPOK_DESC } from "@/lib/constants";
import { StatCard, PageHeader } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { Button } from "@/components/ui/button";
import { RekapSuratBulanan } from "@/components/RekapSuratBulanan";
import { useAuth } from "@/context/AuthContext";

const Hero = ({ s, name, rtLabel }) => {
  const now = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0b1f33] text-white mb-6 fade-up shadow-xl" data-testid="dashboard-hero">
      <img src="/login-bg-2.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b1f33] via-[#0c2a3d]/85 to-transparent" />
      <div className="papua-trim relative" />
      <div className="relative p-6 sm:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-end">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-amber-400">{now}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Selamat datang, {name}</h1>
          <p className="text-emerald-50/80 mt-2 max-w-xl text-sm sm:text-base">Tata Administrasi Penduduk Akurat & Rekapitulasi Umum Inauga — ringkasan {rtLabel}.</p>
        </div>
        <div className="flex gap-6 sm:gap-10">
          {[["Penduduk", s.total], ["KK", s.kk], ["OAP", s.oap], ["Miskin", s.miskin]].map(([l, val]) => (
            <div key={l}><div className="font-display text-3xl font-bold text-amber-400" data-testid={`hero-${l.toLowerCase()}`}>{Number(val || 0).toLocaleString("id-ID")}</div><div className="text-xs text-emerald-50/70">{l}</div></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [rt, setRt] = useState("");
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/stats", { params: rt ? { rt } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [rt]);

  const s = data?.summary || {};
  const usia = ["bayi", "balita", "anak", "produktif", "lansia"].map((k) => ({
    name: k[0].toUpperCase() + k.slice(1), jumlah: s[k] || 0,
  }));
  const oap = [{ name: "OAP", value: s.oap || 0 }, { name: "Non-OAP", value: s.non_oap || 0 }];

  return (
    <div data-testid="dashboard-page">
      <Hero s={s} name={user?.name} rtLabel={rt ? `wilayah RT ${pad(rt)}` : "seluruh Kelurahan Inauga"} />
      <PageHeader kicker={rt ? `Wilayah RT ${pad(rt)}` : "Seluruh Kelurahan Inauga"} title="Dashboard Kependudukan">
        <FieldSelect value={rt} onChange={setRt} options={RT_LIST.map((n) => ({ value: n, label: `RT ${pad(n)}` }))} allLabel="Semua RT" testid="dashboard-rt-filter" className="w-40" />
        <Button variant="outline" onClick={() => downloadExcel(rt).catch((e) => toast.error(errMsg(e)))} data-testid="dashboard-export-button">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Ekspor Excel
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testid="stat-total" label="Total Penduduk" value={s.total} sub={`${s.laki || 0} laki-laki · ${s.perempuan || 0} perempuan`} icon={Users} />
        <StatCard testid="stat-kk" label="Kepala Keluarga" value={s.kepala_keluarga} sub={`${s.kk || 0} nomor KK terdaftar`} icon={Home} tone="sky" delay={40} />
        <StatCard testid="stat-oap" label="Warga OAP" value={s.oap} sub="Orang Asli Papua" icon={Feather} delay={80} />
        <StatCard testid="stat-non-oap" label="Warga Non-OAP" value={s.non_oap} sub={`Pendatang · ${s.wna || 0} WNA`} icon={Plane} tone="amber" delay={120} />
        <StatCard testid="stat-produktif" label="Usia Produktif" value={s.produktif} sub="15–64 tahun" icon={Briefcase} tone="sky" delay={160} />
        <StatCard testid="stat-tidak-produktif" label="Tidak Produktif" value={s.tidak_produktif} sub="0–14 & 65+ tahun" icon={Users} tone="slate" delay={200} />
        <StatCard testid="stat-bayi" label="Bayi & Balita" value={(s.bayi || 0) + (s.balita || 0)} sub={`${s.bayi || 0} bayi · ${s.balita || 0} balita`} icon={Baby} tone="amber" delay={240} />
        <StatCard testid="stat-lansia" label="Lansia" value={s.lansia} sub="65 tahun ke atas" icon={HeartHandshake} tone="slate" delay={280} />
        <StatCard testid="stat-miskin" label="Warga Miskin" value={s.miskin} sub={`${s.kk_miskin || 0} KK miskin`} icon={TrendingDown} tone="rose" delay={320} />
        <StatCard testid="stat-tidak-mampu" label="Warga Tidak Mampu" value={s.tidak_mampu} sub={`${s.kk_tidak_mampu || 0} KK rentan`} icon={TrendingDown} tone="amber" delay={360} />
        <StatCard testid="stat-mampu" label="Warga Mampu" value={s.mampu} icon={Users} delay={400} />
        <StatCard testid="stat-disabilitas" label="Penyandang Disabilitas" value={s.disabilitas} icon={Accessibility} tone="sky" delay={440} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border rounded-xl p-5 lg:col-span-2" data-testid="chart-usia">
          <h3 className="font-semibold text-slate-900">Kelompok Usia</h3>
          <p className="text-xs text-slate-500">{Object.entries(KELOMPOK_DESC).map(([k, v]) => `${k} ${v}`).join(" · ")}</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={usia}>
                <XAxis dataKey="name" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="jumlah" fill="#0b4f4a" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="chart-oap">
          <h3 className="font-semibold text-slate-900">OAP vs Non-OAP</h3>
          <div className="h-52 mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={oap} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={0}>
                  <Cell fill="#059669" />
                  <Cell fill="#d97706" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-emerald-600" />OAP {s.oap || 0}</span>
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-amber-600" />Non-OAP {s.non_oap || 0}</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
            Garis kemiskinan acuan: {rupiah(data?.garis_kemiskinan)}/kapita/bulan.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border rounded-xl p-5 lg:col-span-2" data-testid="chart-per-rt">
          <h3 className="font-semibold text-slate-900">Penduduk per RT — OAP & Non-OAP</h3>
          <p className="text-xs text-slate-500">Komposisi warga aktif di RT 01–16</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer>
              <BarChart data={(data?.per_rt || []).map((r) => ({ ...r, name: `RT ${pad(r.rt)}` }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e2d8" />
                <XAxis dataKey="name" fontSize={11} interval={0} angle={-35} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="oap" name="OAP" stackId="a" fill="#059669" />
                <Bar dataKey="non_oap" name="Non-OAP" stackId="a" fill="#d97706" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="chart-ekonomi">
          <h3 className="font-semibold text-slate-900">Status Ekonomi Warga</h3>
          <div className="h-56 mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={[{ name: "Miskin", value: s.miskin || 0 }, { name: "Tidak Mampu", value: s.tidak_mampu || 0 }, { name: "Mampu", value: s.mampu || 0 }]} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={0}>
                  <Cell fill="#e11d48" />
                  <Cell fill="#f59e0b" />
                  <Cell fill="#0284c7" />
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="bg-white border rounded-xl p-5 lg:col-span-2" data-testid="chart-miskin-rt">
          <h3 className="font-semibold text-slate-900">Sebaran Warga Miskin & Tidak Mampu per RT</h3>
          <div className="h-60 mt-4">
            <ResponsiveContainer>
              <AreaChart data={(data?.per_rt || []).map((r) => ({ ...r, name: pad(r.rt) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e2d8" />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip labelFormatter={(l) => `RT ${l}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="miskin" name="Miskin" stroke="#e11d48" fill="#e11d48" fillOpacity={0.25} />
                <Area type="monotone" dataKey="tidak_mampu" name="Tidak Mampu" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="chart-gender">
          <h3 className="font-semibold text-slate-900">Jenis Kelamin</h3>
          <div className="h-52 mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={[{ name: "Laki-laki", value: s.laki || 0 }, { name: "Perempuan", value: s.perempuan || 0 }]} dataKey="value" outerRadius={80}>
                  <Cell fill="#0b4f4a" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-2" data-testid="stat-total-surat">Surat diterbitkan: <b className="text-slate-900">{data?.total_surat || 0}</b></div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-semibold text-slate-900 mb-3">Sebaran per RT</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {(data?.per_rt || []).map((r) => (
            <button key={r.rt} onClick={() => nav(`/warga?rt=${r.rt}`)} data-testid={`rt-tile-${r.rt}`}
              className="text-left bg-white border rounded-xl p-3 transition-colors duration-200 hover:border-[#0b4f4a] hover:bg-emerald-50/40">
              <div className="text-xs font-semibold text-amber-700">RT {pad(r.rt)}</div>
              <div className="font-display text-2xl font-bold text-slate-900">{r.total}</div>
              <div className="text-[11px] text-slate-500">{r.kepala_keluarga} KK · {r.miskin} miskin</div>
            </button>
          ))}
        </div>
      </div>

      <RekapSuratBulanan />

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <TopList title="Pekerjaan Terbanyak" items={data?.pekerjaan} testid="list-pekerjaan" />
        <TopList title="Tingkat Pendidikan" items={data?.pendidikan} testid="list-pendidikan" />
      </div>
    </div>
  );
}

const TopList = ({ title, items = [], testid }) => {
  const max = Math.max(1, ...items.map((i) => i.jumlah));
  return (
    <div className="bg-white border rounded-xl p-5" data-testid={testid}>
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      {items.length === 0 && <p className="text-sm text-slate-500">Belum ada data.</p>}
      <div className="space-y-2.5">
        {items.slice(0, 8).map((i) => (
          <div key={i.label}>
            <div className="flex justify-between text-xs"><span>{i.label}</span><b>{i.jumlah}</b></div>
            <div className="h-1.5 bg-slate-100 rounded mt-1"><div className="h-full bg-amber-500 rounded" style={{ width: `${(i.jumlah / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};
